import { ContextMenu, LockIcon, PinIcon, RoomIcon } from '@bp/ui-components';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';
import { useDebounceCallback } from 'usehooks-ts';
import { useTeachingBlockCardMenu } from '../../../hooks/useTeachingBlockCardMenu';
import { useTeachingBlockStore } from '../Contexts/TeachingBlockContext';
import { Item, TeachingBlockVirtualCard } from '../types';
import styles from './VirtualGridCard.module.scss';

type VirtualGridCardProps = {
  card: TeachingBlockVirtualCard;
  row: Item;
};

export const VirtualGridCard = observer(({ card, row }: VirtualGridCardProps) => {
  const store = useTeachingBlockStore();

  const parent = store.getParentCard(card.parentUuid);
  if (!parent) throw new Error('parent is undefined');

  const isPicked = parent.uuid === store.pickedCard?.card.uuid;
  const isPinned = parent.uuid === store.pinnedCard?.uuid;
  const isLocked = parent.locked;

  const hasWarnings = !!parent.warnings && parent.warnings.length > 0;
  const hasAvailabilityWarnings = !!parent.availabilityWarnings && parent.availabilityWarnings.length > 0;
  const showWarnings = store.highlightMode.includes('warnings') && (hasWarnings || hasAvailabilityWarnings);

  const borders = card.duration > 7 ? Math.floor((card.duration / 7) * 2) - 1 : 1;
  let offset = dayjs(card.startDateString).day() - 1;
  if (offset < 0) offset = 6;

  const gridCard =
    store.gridContext === 'classes'
      ? parent.classGridCards.find((c) => c.classUuid === row?.value)
      : store.gridContext === 'teachers'
        ? parent.teacherGridCards.find((t) => t.teacherUuid === row?.value)
        : store.gridContext === 'rooms'
          ? parent.roomGridCards.find((r) => r.roomUuid === row?.value)
          : null;

  function onCardEditOpen() {
    store.setCardDurationModal({
      isOpen: true,
      card: parent ?? undefined,
      isVirtual: true,
    });
  }

  const { menu, hasRooms } = useTeachingBlockCardMenu({
    card: parent,
    styles,
    hasWarnings,
    hasAvailabilityWarnings,
    isPicked,
    isPinned,
    onCardEditOpen,
  });

  const menuClasses = classNames(styles.menu, { [styles['has-warning']]: showWarnings });

  const cardClasses = classNames(styles['virtual-grid-card'], {
    [styles.picked]: isPicked,
    [styles.pinned]: isPinned,
    [styles.locked]: isLocked,
    [styles.warning]: showWarnings,
    [styles.room]: store.highlightMode.includes('rooms') && !hasRooms,
    // [styles['high-light']]:
    //   pinboardStore.controlMode.isActive &&
    //   pinboardStore.controlMode.highlights.length !== 0 &&
    //   pinboardStore.controlMode.highlights.includes(card.uuid),
    // [styles['low-light']]:
    //   pinboardStore.controlMode.isActive &&
    //   pinboardStore.controlMode.highlights.length !== 0 &&
    //   !pinboardStore.controlMode.highlights.includes(card.uuid),
  });

  const debounceOnCardHover = useDebounceCallback(() => {
    store.selectCard(toJS(parent));
  }, 500);

  const onMouseEnter = useCallback(() => {
    if (!store.selectedCard) {
      store.selectCard(toJS(parent));
    } else {
      debounceOnCardHover();
    }
  }, [parent, debounceOnCardHover, store.selectCard, store.selectedCard]);

  const onMouseLeave = useCallback(() => {
    debounceOnCardHover.cancel();
  }, [debounceOnCardHover]);

  const onOpenChange = useCallback(() => {
    debounceOnCardHover.cancel();
    store.selectCard(toJS(parent));
  }, [parent, debounceOnCardHover, store.selectCard]);

  return (
    <>
      <ContextMenu
        className={menuClasses}
        data={menu}
        parent={document.getElementById('teaching-block-grid')}
        onOpenChange={onOpenChange}
      >
        <div
          key={card.uuid}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          onClick={(event) => store.pickCard(parent, { x: event.pageX, y: event.pageY })}
          title={parent.label}
          className={cardClasses}
          style={{
            left: `round(nearest, ${(offset / 7) * 100}%, 1px)`,
            height: gridCard?.cssHeight ?? '100%',
            width: `round(nearest, calc(${(card.duration / 7) * 100}% + ${borders}px), 1px)`,
            top: gridCard?.cssTop ?? '0%',
            background: gridCard?.cssGradientString ?? 'var(--color-grey)',
            color: gridCard?.textColor ?? 'var(--grid-card-text)',
          }}
        >
          <div className={styles.title}>{parent.labelShort}</div>
          {!store.highlightMode.includes('noIcons') && (isPinned || isLocked || !hasRooms) && (
            <div className={styles.info}>
              {isPinned && <PinIcon className={styles.icon} />}
              {isLocked && <LockIcon className={styles.icon} />}
              {!hasRooms && <RoomIcon className={styles.icon} />}
            </div>
          )}
        </div>
      </ContextMenu>
    </>
  );
});
